import { useEffect, useState } from 'react';
import {
  IconBook,
  IconChart,
  IconDumbbell,
  IconGear,
  IconHeart,
  IconToday,
} from './components/Icons';
import { describeBlock } from './engine/blocks';
import { blockInfo } from './engine/blocks';
import { useApp } from './store/AppStore';
import { Cardio } from './views/Cardio';
import { Library } from './views/Library';
import { Onboarding } from './views/Onboarding';
import { Progress } from './views/Progress';
import { Settings } from './views/Settings';
import { Today } from './views/Today';
import { Workout } from './views/Workout';

type Tab = 'today' | 'cardio' | 'library' | 'progress' | 'settings';

const TABS: { id: Tab; label: string; Icon: typeof IconToday }[] = [
  { id: 'today', label: 'Today', Icon: IconToday },
  { id: 'cardio', label: 'Cardio', Icon: IconHeart },
  { id: 'library', label: 'Library', Icon: IconBook },
  { id: 'progress', label: 'Progress', Icon: IconChart },
  { id: 'settings', label: 'Settings', Icon: IconGear },
];

export function App() {
  const { state, syncHealth } = useApp();
  const [tab, setTab] = useState<Tab>('today');
  const active = state.activeWorkout;

  const healthReady = state.settings.health.enabled && Boolean(state.settings.health.token);

  // Pull Health data once per app open. Deliberately silent: a failed sync is
  // not worth interrupting you over, and you'll be offline at the gym half the
  // time anyway. Settings → Sync now reports errors properly.
  useEffect(() => {
    if (!healthReady) return;
    void syncHealth().catch(() => undefined);
    // Only on mount, and only when the connection is configured.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [healthReady]);

  // Jumping between tabs mid-workout shouldn't dump you halfway down the page.
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [tab]);

  const block = blockInfo(state.settings);

  if (!state.settings.onboarded) return <Onboarding />;

  return (
    <div className="app">
      <header className="topbar">
        <span className="brand">Iron Ledger</span>
        <span className="meta">
          {active ? (
            <span style={{ color: 'var(--accent)' }}>{active.templateName} in progress</span>
          ) : (
            describeBlock(block)
          )}
        </span>
      </header>

      <main>
        {tab === 'today' &&
          (active ? (
            <Workout onFinished={() => setTab('today')} />
          ) : (
            <Today onStarted={() => setTab('today')} onGoCardio={() => setTab('cardio')} />
          ))}
        {tab === 'cardio' && <Cardio />}
        {tab === 'library' && <Library />}
        {tab === 'progress' && <Progress />}
        {tab === 'settings' && <Settings />}
      </main>

      <nav className="tabbar" aria-label="Main">
        {TABS.map(({ id, label, Icon }) => {
          const isWorkoutTab = id === 'today' && Boolean(active);
          return (
            <button
              key={id}
              className={tab === id ? 'active' : ''}
              onClick={() => setTab(id)}
              aria-current={tab === id ? 'page' : undefined}
            >
              {isWorkoutTab ? (
                <IconDumbbell className="tab-icon" />
              ) : (
                <Icon className="tab-icon" />
              )}
              {isWorkoutTab ? 'Workout' : label}
              {isWorkoutTab && tab !== id && <span className="dot" />}
            </button>
          );
        })}
      </nav>
    </div>
  );
}
